import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, DatePicker, Space, message, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import ReportService from "../../services/ReportService";
import { formatToTL } from "../../utils";

const { RangePicker } = DatePicker;

type ProposalSummary = {
  offerCount: number;
  offerTotal: number;
  offerAvg: number;
  contractCount: number;
  contractTotal: number;
  contractAvg: number;
  contractRatio: number;
  negativeCount: number;
  negativeTotal: number;
  negativeAvg: number;
  negativeRatio: number;
  contractAmountRatio: number;
  negativeAmountRatio: number;
};

type ProposalReport = {
  firstName: string;
  lastName: string;
  email: string;
  accountId: number;
  offerCount: number;
  offerTotal: number;
  offerAvg: number;
  contractCount: number;
  contractTotal: number;
  contractAvg: number;
  contractRatio: number;
  negativeCount: number;
  negativeTotal: number;
  negativeAvg: number;
  negativeRatio: number;
  contractAmountRatio: number;
  negativeAmountRatio: number;
};

const columns: ColumnsType<ProposalReport> = [
  {
    title: "Ad Soyad / E-posta",
    render: (_, r) => (
      <>
        {r.firstName} {r.lastName}
        <br />
        <span style={{ color: "#888", fontSize: 12 }}>{r.email}</span>
      </>
    ),
    key: "adsoyad",
    fixed: "left",
    width: 180
  },
  {
    title: (
      <div>
        <h4 style={{ margin: 0 }}>Teklif</h4>
      </div>
    ),
    children: [
      { title: "Sayısı", dataIndex: "offerCount", key: "offerCount", align: "center", width: 80 },
      { title: "Toplamı", dataIndex: "offerTotal", key: "offerTotal", render: (v: number) => formatToTL(v), align: "right", width: 110 },
      { title: "Ortalaması", dataIndex: "offerAvg", key: "offerAvg", render: (v: number) => formatToTL(v), align: "right", width: 110 }
    ]
  },
  {
    title: (
      <div>
        <h4 style={{ margin: 0 }}>Sözleşme</h4>
      </div>
    ),
    children: [
      { title: "Sayısı", dataIndex: "contractCount", key: "contractCount", align: "center", width: 80 },
      { title: "Toplamı", dataIndex: "contractTotal", key: "contractTotal", render: (v: number) => formatToTL(v), align: "right", width: 110 },
      { title: "Ortalaması", dataIndex: "contractAvg", key: "contractAvg", render: (v: number) => formatToTL(v), align: "right", width: 110 },
      {
        title: "Oranı",
        dataIndex: "contractRatio",
        key: "contractRatio",
        render: (v: number) => `%${Math.round(v * 100)}`,
        align: "center",
        width: 90
      }
    ]
  },
  {
    title: (
      <div>
        <h4 style={{ margin: 0 }}>Olumsuz</h4>
      </div>
    ),
    children: [
      { title: "Sayısı", dataIndex: "negativeCount", key: "negativeCount", align: "center", width: 80 },
      { title: "Toplamı", dataIndex: "negativeTotal", key: "negativeTotal", render: (v: number) => formatToTL(v), align: "right", width: 110 },
      { title: "Ortalaması", dataIndex: "negativeAvg", key: "negativeAvg", render: (v: number) => formatToTL(v), align: "right", width: 110 },
      {
        title: "Oranı",
        dataIndex: "negativeRatio",
        key: "negativeRatio",
        render: (v: number) => `%${Math.round(v * 100)}`,
        align: "center",
        width: 90
      }
    ]
  }
];

const ProposalReportAllDashboard: React.FC = () => {
  // Varsayılan olarak bu ay başı ve bugün seçili
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs()
  ]);
  const [summary, setSummary] = useState<ProposalSummary | null>(null);
  const [tableData, setTableData] = useState<ProposalReport[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    const [startDate, endDate] = range;
    if (!startDate || !endDate) {
      message.warning("Lütfen tarih aralığı seçiniz.");
      return;
    }

    setLoading(true);
    try {
      const apiStart = startDate.isValid() ? startDate.format("YYYY-MM-DD") : "";
      const apiEnd = endDate.isValid() ? endDate.format("YYYY-MM-DD") : "";

      const [summaryRes, tableRes] = await Promise.all([
        ReportService.getProposalSummary(apiStart, apiEnd),
        ReportService.getProposalReportStats(apiStart, apiEnd)
      ]);
      setSummary(summaryRes.data as ProposalSummary);
      setTableData(tableRes.data as ProposalReport[]);
    } catch (err: any) {
      setSummary(null);
      setTableData([]);
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        "Rapor alınırken hata oluştu!"
      );
    } finally {
      setLoading(false);
    }
  };

  // İlk açılışta veriyi getir
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Card
        title="İş Ortağı Teklif/Sözleşme Performansı"
        extra={
          <Space>
            <RangePicker
              value={range}
              onChange={values => setRange(values as [Dayjs, Dayjs])}
              format="DD/MM/YYYY"
              placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
              allowClear={false}
            />
            <Button type="primary" onClick={fetchAll} loading={loading}>
              Raporu Getir
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card
              title={<span style={{ color: "#1764b2" }}>Teklif</span>}
              bordered={false}
              style={{ background: "#f6faff" }}
              bodyStyle={{ padding: 16, minHeight: 140 }}
              loading={loading}
            >
              <div>
                <div>
                  <b>Sayısı:</b> {summary?.offerCount ?? "-"}
                </div>
                <div>
                  <b>Toplamı:</b> {formatToTL(summary?.offerTotal ?? 0)}
                </div>
                <div>
                  <b>Ortalaması:</b> {formatToTL(summary?.offerAvg ?? 0)}
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title={<span style={{ color: "#34a853" }}>Sözleşme</span>}
              bordered={false}
              style={{ background: "#f7fbf6" }}
              bodyStyle={{ padding: 16, minHeight: 140 }}
              loading={loading}
            >
              <div>
                <div>
                  <b>Sayısı:</b> {summary?.contractCount ?? "-"}
                </div>
                <div>
                  <b>Toplamı:</b> {formatToTL(summary?.contractTotal ?? 0)}
                </div>
                <div>
                  <b>Ortalaması:</b> {formatToTL(summary?.contractAvg ?? 0)}
                </div>
                <div>
                  <b>Teklif Sayısı Olumlu Oran:</b> {summary ? `%${Math.round((summary.contractRatio ?? 0) * 100)}` : "-"}
                </div>
                <div>
                  <b>Teklif Tutarı Olumlu Oranı:</b> {summary ? `%${Math.round((summary.contractAmountRatio ?? 0) * 100)}` : "-"}
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title={<span style={{ color: "#ea4335" }}>Olumsuz</span>}
              bordered={false}
              style={{ background: "#fff6f6" }}
              bodyStyle={{ padding: 16, minHeight: 140 }}
              loading={loading}
            >
              <div>
                <div>
                  <b>Sayısı:</b> {summary?.negativeCount ?? "-"}
                </div>
                <div>
                  <b>Toplamı:</b> {formatToTL(summary?.negativeTotal ?? 0)}
                </div>
                <div>
                  <b>Ortalaması:</b> {formatToTL(summary?.negativeAvg ?? 0)}
                </div>
                <div>
                  <b>Teklif Sayısı Olumsuz Oran:</b> {summary ? `%${Math.round((summary.negativeRatio ?? 0) * 100)}` : "-"}
                </div>
                <div>
                  <b>Teklif Tutarı Olumsuz Oranı:</b>   {summary ? `%${Math.round((summary.negativeAmountRatio ?? 0) * 100)}` : "-"}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
        <Row style={{ marginTop: 24 }}>
          <Table
            style={{ width: "100%" }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            rowKey="accountId"
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: "Veri bulunamadı. Lütfen tarih aralığı seçip sorgulayınız."
            }}
            bordered
            size="small"
            pagination={false}
          />
        </Row>
      </Card>
    </div>
  );
};

export default ProposalReportAllDashboard;
